import { RouterClient } from "../../../../clients/routerClient";
import { Logger } from "../../../../clients/logger";
import { WrapManager as MyWrapManager } from "./WrapManager";
import { WindowPoolSingleton } from "../Common/Pools/PoolSingletons";
import * as util from "../../../../common/util";
import { System } from "../../../../common/system";
import { publishWrapState } from "../Common/publishWrapState";
import { FinsembleWindowInternal } from "../WindowAbstractions/FinsembleWindowInternal";
import { BaseWindow } from "../WindowAbstractions/BaseWindow";
import { WindowIdentifier } from "../types";
import { generateDynamicAuthenticationToken, updateCustomDataWithAuthToken } from "./componentUtils";
import { InteropAdmin } from "../../../../clients/Interop/InteropAdmin";
import { FEAWindow } from "../../../../FEA";
import { StandardPromise } from "../../../../types";
import _get from "lodash/get";
import { merge } from "lodash";

// strip URL of any parameters or fragments and return result
const stripUrl = (url) => url?.split(/[#?]/)[0];

const FREESTANDING_CUSTOMDATA_OVERRIDES = {
	foreign: {
		services: {
			windowService: {
				allowTabbing: false,
				allowTiling: false,
				allowGrouping: false,
			},
		},
	},
	component: {
		type: "Freestanding",
	},
	window: {
		addToWorkspace: false,
	},
};
export class CreateSplinterAndInject {
	finsembleConfig: any;

	eventInterruptors: any;

	manifest: any;

	stackedWindowManager: any;

	isWindows: boolean | undefined;

	interopAdmin: InteropAdmin;

	constructor(manifest) {
		this.manifest = manifest;
		this.finsembleConfig = manifest.finsemble;
		this.bindAllFunctions();
		this.eventInterruptors = {};
		this.interopAdmin = new InteropAdmin();
	}

	windowServiceChannelName(channelTopic) {
		return `WindowService-Request-${channelTopic}`;
	}

	bindAllFunctions() {
		const self = this;
		for (const name of Object.getOwnPropertyNames(Object.getPrototypeOf(self))) {
			if (self[name] instanceof Function) {
				self[name] = self[name].bind(self); // only bind function properties
			}
		}
	}

	/**
	 * NOTE: This function is not currently used, but keeping as a prototype for matching URLs to automatically map window.open(url) to a FDC3 app name.
	 * Return the matching component type.  If the new window's URL has a component definition with the same URL, then assume the component definition is the correct one
	 * and use its component type.  If no match is found, return "unknown-component-type".
	 *
	 * @private
	 * @param {*} url the url to find a match for in the component map
	 * @param {*} components map of all components configured in Finsemble
	 * @returns {(any | null)}
	 * @memberof CreateSplinterAndInject
	 */
	private matchingComponentConfigByURL(url, components): any | null {
		Logger.system.debug(" matchingComponentConfigByURL start", url, components);
		let matchingComponent;

		// if the window URL or the component config URL has parameters then strip them before comparing for a match
		let strippedWindowUrl = stripUrl(url);
		let matchingComponents: any[] = Object.values(components).filter(
			(componentConfig: any) => stripUrl(componentConfig.window?.url) === strippedWindowUrl
		);

		if (matchingComponents.length === 0) {
			matchingComponent = null;
			// don't log a warning because there are valid cases where the window being opened doesn't have a config entry
			Logger.system.debug(
				`matchingComponentConfigByURL: opened window URL ${strippedWindowUrl} does not have a matching component type in Finsemble config`,
				components
			);
		} else if (matchingComponents.length === 1) {
			matchingComponent = matchingComponents[0];
			Logger.system.debug(
				`matchingComponentConfigByURL: opened window URL  ${strippedWindowUrl} assigned to matching component`,
				matchingComponent
			);
		} else {
			matchingComponent = matchingComponents[0];
			Logger.system.warn(
				`matchingComponentConfigByURL: opened window URL  ${strippedWindowUrl} has duplicate matching URLs in Finsemble Config so assigned to first matching component`,
				matchingComponent
			);
		}

		return matchingComponent;
	}

	/**
	 * NOTE: This function is not currently used, but keeping as a prototype to update options using an existing component config (in combination with matchingComponentConfigByURL).
	 * For the externally opened window, fill in essential option properties using a matching component from config.
	 * For example, the correct component type must be set for FDC3 to find the corresponding appD entry in the InteropService.
	 *
	 * @private
	 * @param {*} windowOptions the window options to be updated
	 * @param {*} matchingComponentConfig the matching component
	 * @returns {*} updated window options
	 * @memberof CreateSplinterAndInject
	 */
	private updateOptionsWithMatchingComponentConfig(windowOptions, matchingComponentConfig): any {
		if (matchingComponentConfig) {
			windowOptions.componentType = matchingComponentConfig?.component.type;
			windowOptions.customData = windowOptions.customData ?? {};
			windowOptions.customData.component = windowOptions.customData.component ?? {};
			windowOptions.customData.component.type = matchingComponentConfig?.component.type;
			windowOptions.customData.component.displayName = matchingComponentConfig?.component.displayName;
		}
		return windowOptions;
	}

	/**
	 * Wrapper around get Options that returns a promise.
	 * @param theWrap A FinsembleWindow wrap
	 */
	private getWrapOptions(wrap) {
		return new Promise((resolve) => {
			// safe to ignore e. it'll be logged in the getOptions method
			wrap._getOptions(null, (e, o) => {
				resolve(o);
			});
		});
	}

	/**
	 * This method gets called when adding a new native-library window
	 */
	async addNewNativeWindow({ appUUID, name, windowType, options, appName }) {
		Logger.system.debug(`addNewNativeWindow: { appUUID, name, windowType}: ${[appUUID, name, windowType]}}`);

		// Let the system know that the wrap has been created, but not ready yet.
		publishWrapState(name, "created");

		// WindowIdentifier, used to get a wrap.
		const windowIdentifier: WindowIdentifier = {
			windowName: name,
			uuid: appUUID,
			windowType,
		};

		await BaseWindow._createWrap({
			windowIdentifier,
			name,
			setWindowType: windowType,
		});

		const { wrap } = await FinsembleWindowInternal.getInstance({
			waitForReady: false,
			uuid: windowIdentifier.uuid,
			name: windowIdentifier.windowName,
			windowType: windowIdentifier.windowType,
		});

		wrap.key ??= wrap.uuid;

		// The wrap for a native window launched outside of Finsemble sends ready before
		// the FinsembleWindow instance is listening. When the wrap is fetched here,
		// we'll trigger ready again so the FinsembleWindow instance can finish initializing
		RouterClient.addListener(`${name}.onSpawned`, () => {
			wrap.eventManager.trigger("ready");
		});

		// if windows was opened with parameters, save it here
		const defaultOptions = await this.getWrapOptions(wrap);
		let opts = merge(defaultOptions, options);
		opts.name ??= name;

		// imesner: 20230110 this needs help
		const appFromConfig =
			this.finsembleConfig.apps.find((a) => a.appId === appName) ??
			this.finsembleConfig.apps.find((a) => a.name === appName);

		if (appFromConfig?.hostManifests?.Finsemble) {
			opts = merge(opts, appFromConfig?.hostManifests?.Finsemble);
		}

		// Native windows that aren't added/appropriated from a parent with options won't have
		// any options here. Initializing with empty objects will disable appropriate functions
		// like snapping/docking/etc
		opts.customData = merge(opts.customData, FREESTANDING_CUSTOMDATA_OVERRIDES);

		WindowPoolSingleton.add(name, wrap);

		wrap.windowDescriptor = {
			name,
			...windowIdentifier,
			...opts,
			componentType: "Freestanding",
		};

		wrap.descriptor = wrap.windowDescriptor;

		// Set essential interop data now to ensure it will be available for FDC3 client registration withing the InteropService.
		// The windowDescriptor might still be modified within spawnWithUpdatedData, but not the properties needed for interop.
		this.interopAdmin.setInteropRegistrationData(wrap.windowDescriptor);

		publishWrapState(opts.name, "ready");

		MyWrapManager.add({
			windowIdentifier: {
				windowName: wrap.windowName,
				uuid: wrap.descriptor.uuid,
				componentType: wrap.descriptor?.componentType,
				windowType: wrap.identifier?.windowType,
			},
			windowDescriptor: wrap.windowDescriptor,
		});

		return wrap;
	}

	/**
	 * This method gets called when someone calls `window.open`. We capture an event bubbled up from
	 * FEA, and then 'appropriate' that window so that all of the finsemble stuff will work with it.
	 * This method makes sure the window can participate in finsemble things. Without a wrap that
	 * the windowService knows of, it won't be able to do anything.
	 */
	async addExternallyOpenedWindow({ appUUID, name, windowType = "WebWindow", options, appName }) {
		Logger.system.debug(`addExternallyOpenedWindow: { appUUID, name, windowType}: ${[appUUID, name, windowType]}}`);
		const modifyWindowOptions = (opts, windowIdentifier: WindowIdentifier) => {
			const windowOptions = JSON.parse(JSON.stringify(opts));
			// userDefined components pull from customData's url for some reason...
			// this was set to the parent's url because of a merge in FEA.
			if (windowOptions.url) {
				windowOptions.customData.window.url = windowOptions.url;
			}

			windowOptions.name = windowIdentifier.windowName;

			// Native windows that aren't added/appropriated from a parent with options won't have
			// any options here. Initializing with empty objects will disable appropriate functions
			// like snapping/docking/etc
			if (!windowOptions.customData) {
				windowOptions.customData = FREESTANDING_CUSTOMDATA_OVERRIDES;
			}

			if (_get(windowOptions, "customData.window.addToWorkspace")) {
				windowOptions.customData.window.addToWorkspace = false;
			}

			// Tabbing, Tiling, and Grouping are disallowed, as those features are all intimately tied into
			// the workspace
			if (_get(windowOptions, "customData.foreign.services.windowService.allowTabbing")) {
				windowOptions.customData.foreign.services.windowService.allowTabbing = false;
			}

			if (_get(windowOptions, "customData.foreign.services.windowService.allowTiling")) {
				windowOptions.customData.foreign.services.windowService.allowTiling = false;
			}

			if (windowOptions?.customData?.foreign?.services?.windowService?.allowGrouping) {
				windowOptions.customData.foreign.services.windowService.allowGrouping =
					this.manifest.finsemble.servicesConfig.docking.allowAppropriatedWindowsToGroup || false;
			}

			return windowOptions;
		};
		/*
		 * Pushes the updated options down to the FEA layer.
		 * At this point, FSBL may or may not be initializing in the child window.
		 * It will be waiting for a wrap to be returned from `getInstance`. The wrap won't return
		 * until we publish 'ready' down below.
		 *
		 * If the order of this code changes, you'll introduce the opportunity for some
		 * really nasty race conditions.
		 * There is no need to wait for this to finish before proceeding.
		 */
		const updateWindowOptions = (opts, wrap) => {
			wrap._updateOptions(opts);
		};

		/**
		 * Adds the window to the window pool.
		 * @param opts WindowOptions
		 * @param wrap FinsembleWindow wrap
		 */
		const addToWindowPool = (opts, wrap) => {
			WindowPoolSingleton.add(opts.name, wrap);
		};

		/**
		 * Helper that returns a wrap instead of { wrap }
		 * @param wi windowIdentifier
		 */
		const getWrap = async (wi: WindowIdentifier) => {
			// no need to wait here. We know that it's ready on the FEA side and we can start using it.
			const { wrap } = await FinsembleWindowInternal.getInstance({
				waitForReady: false,
				uuid: wi.uuid,
				name: wi.windowName,
				windowType: wi.windowType,
			});
			wrap.key ??= wrap.uuid;
			return wrap;
		};

		// Let the system know that the wrap has been created, but not ready yet.
		publishWrapState(name, "created");
		// WindowIdentifier, used to get a wrap.
		const windowIdentifier: WindowIdentifier = {
			windowName: name,
			uuid: appUUID,
			windowType,
		};
		BaseWindow._createWrap({
			windowIdentifier,
			name,
			setWindowType: windowType,
		});

		const wrap = await getWrap(windowIdentifier);

		// if windows was opened with parameters, save it here
		const defaultOptions = await this.getWrapOptions(wrap);
		let opts = merge(defaultOptions, options);

		// adjust the window options as needed, potentially adjusting the component type and custom data
		let modifiedWindowOptions = modifyWindowOptions(opts, windowIdentifier);

		// Use the component type OR the component name if components type is not present
		// to generate auth token and add to custom data passed to new window.
		// Note - When a Freestanding native app is spawned, it does not contain a component type
		// component name must be used instead, otherwise we will get the notification
		// showing ""undefined" registered without an appD entry in config..."
		//
		// Although all windows being spawned will not need an authentication token, it's easier to always generate than to discern when needed.
		const authenticationToken = generateDynamicAuthenticationToken(
			windowIdentifier.windowName,
			modifiedWindowOptions.componentType ?? appName ?? modifiedWindowOptions.name
		);

		modifiedWindowOptions = updateCustomDataWithAuthToken(modifiedWindowOptions, authenticationToken);

		/**
		 * getWrapOptions will return the options from FEA.
		 * Those options will be (more or less) a copy of the options for whatever window
		 * called window.open.
		 *
		 * If the ready is published before we send the updateOptions request down to FEA, the window
		 * will come online with a (potentially) more permissive set of allowed operations.
		 * Don't mess with the order of the code below.
		 */
		updateWindowOptions(modifiedWindowOptions, wrap);
		addToWindowPool(modifiedWindowOptions, wrap);
		wrap.windowDescriptor = {
			...windowIdentifier,
			...modifiedWindowOptions,
			componentType: "Freestanding",
		};

		// Set essential interop data now to ensure it will be available for FDC3 client registration withing the InteropService.
		// The windowDescriptor might still be modified within spawnWithUpdatedData, but not the properties needed for interop.
		this.interopAdmin.setInteropRegistrationData(wrap.windowDescriptor);

		publishWrapState(modifiedWindowOptions.name, "ready");

		MyWrapManager.add({
			windowIdentifier: {
				windowName: modifiedWindowOptions.name,
				uuid: modifiedWindowOptions.uuid,
				componentType: modifiedWindowOptions.componentType,
			},
			windowDescriptor: modifiedWindowOptions,
		});

		return wrap;
	}

	async createWindow(params, callback = Function.prototype) {
		const windowDescriptorName = params?.windowDescriptor?.name || "unknown";
		Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */`CreateSplinterAndInject.createWindow for ${windowDescriptorName}`, params);

		const { windowDescriptor } = params;
		if (!windowDescriptor) {
			Logger.system.error("no windowDescriptor for WindowService-Request.createWindow");
		}

		const { window } = windowDescriptor.customData;
		let { windowType } = window;

		// Construct an object of values that may be used to calculate the windowType
		const winConfig = {
			windowType,
			compound: window.compound,
			type: windowDescriptor.type,
		};
		windowType = util.getWindowType(winConfig);

		if (windowType === "FinsembleNativeWindow" || windowType === "NativeWindow") {
			// make sure native windows have a wrap ASAP
			await FinsembleWindowInternal.getInstance({
				waitForReady: false,
				name: windowDescriptor.name || windowDescriptor.windowName,
				uuid: "N/A",
				windowType: "NativeWindow",
			});
		}

		Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */`CreateSplinterAndInject.createWindow invoking doSpawn ${windowDescriptorName}`);
		const { err, windowIdentifier } = await this.doSpawn(windowDescriptor);
		Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */`CreateSplinterAndInject.createWindow ending doSpawn ${windowDescriptorName}`);

		if (err) {
			Logger.system.error("COMPONENT LIFECYCLE: createWindow error: ", err);
			callback(err);
		} else {
			Logger.system.log("createSplinterAndInject.doSpawn complete", windowIdentifier.name);
			const realWindowIdentifier = {
				name: windowIdentifier.name,
				windowName: windowIdentifier.windowName,
				uuid: windowIdentifier.uuid,
			};

			Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */`CreateSplinterAndInject.createWindow invoking publishWrapState created ${windowDescriptorName}`);
			publishWrapState(windowIdentifier.windowName, "created");

			// For now don't save the fake Citrix or external wraps to our pool. This may need to be revised if we need to track these windows.
			if (windowType !== "Citrix" && windowType !== "external") {
				// This is a bit of a hack, but this sets Citrix window wraps to the fake wrap created to myWrapManager
				const { wrap } = await FinsembleWindowInternal.getInstance({
					waitForReady: false,
					name: realWindowIdentifier.name || realWindowIdentifier.windowName,
				});

				// Add in the uuid to all the places where it needs to be once the window is spawned
				if (windowType === "FinsembleNativeWindow" || windowType === "NativeWindow") {
					wrap.key = windowIdentifier.uuid;
					wrap.uuid = windowIdentifier.uuid;
					wrap.windowOptions.nativeWindowHandle = windowIdentifier.uuid;
				}

				WindowPoolSingleton.add(windowIdentifier.windowName, wrap);
				Logger.system.log(/*'bW9udGVyZXlzIGNvYXN0', */`CreateSplinterAndInject.createWindow invoking publishWrapState ready ${windowDescriptorName}`);
				publishWrapState(windowIdentifier.windowName, "ready");
			}
			callback(null, realWindowIdentifier);
		}
	}

	// probably only a temporary routine -- currently supports public wrapper
	async getWindowIdentifier(params, callback) {
		Logger.system.debug(`"CreateSplinterAndInject.getWindowIdentifier for  ${params.windowName}`, params);
		const { err, data } = await MyWrapManager.get(params);
		callback(err, data);
	}

	async doSpawn(windowDescriptor: any): Promise<{ err: any; windowIdentifier: any }> {
		const { window } = windowDescriptor.customData;
		let { windowType } = window;

		// Construct an object of values that may be used to calculate the windowType
		const winConfig = {
			windowType: windowDescriptor.windowType ?? windowType,
			compound: window.compound,
			type: windowDescriptor.type,
		};
		windowType = util.getWindowType(winConfig);
		if (windowType === "FinsembleNativeWindow" || windowType === "native") {
			windowType = "NativeWindow";
			windowDescriptor.isWPF = true;

			// If running on Electron, Finsemble aware applications will need the IAC server address.
			const iacConfig = this.finsembleConfig.IAC;
			if (iacConfig) {
				// If IAC config exists, pass it on to the window descriptor
				windowDescriptor.IAC = iacConfig;
			}
		}

		windowDescriptor.windowType = windowType;
		Logger.system.debug("WindowService.doSpawn", windowDescriptor, windowType);
		Logger.system.log("WindowService.doSpawn", windowDescriptor.name);
		// Hack to get the wrap before it's ready. Added by ryan, commit d781a.
		const result = {
			windowIdentifier: {
				windowName: windowDescriptor.name,
				uuid: windowDescriptor.uuid,
				componentType: windowDescriptor.componentType,
				monitor: windowDescriptor.monitorInfo,
				windowType: windowDescriptor.windowType,
			},
			windowDescriptor,
		};
		MyWrapManager.add(result);

		Logger.system.debug("WindowService.doSpawn-time before", windowType, windowDescriptor.name);

		// hideOnce is set by the workspace service for hidden tabs. It should override autoShow if set, but only for the initial spawn.
		const { hideOnce } = windowDescriptor;
		let descriptor = Object.assign({}, windowDescriptor);
		if (hideOnce) descriptor.autoShow = false;

		let data;
		let err;
		try {
			switch (windowType) {
				case "NativeWindow":
					({ err, data } = await this.spawnExternalWindow(windowDescriptor));
					if (err) {
						Logger.system.warn(
							`Spawning external window ${windowDescriptor.name} failed - removing entry from wrap manager`
						);
						MyWrapManager.remove(windowDescriptor.windowName);

						return { err: err, windowIdentifier: null };
					} else {
						MyWrapManager.setUuid(result.windowIdentifier.windowName, data.uuid);
					}
					break;
				case "WebWindow":
				//backwards compatibility for windowType application removal
				case "WebApplication":
					({ err, data } = await this.spawnWebWindow(descriptor, windowType === "WebApplication"));
					if (err) {
						Logger.system.warn(
							`${windowType} - Spawning web window ${windowDescriptor.windowName} failed - removing entry from wrap manager`
						);
						MyWrapManager.remove(windowDescriptor.windowName);
						return { err: err, windowIdentifier: null };
					} else {
						MyWrapManager.setUuid(result.windowIdentifier.windowName, data.uuid);
					}
					break;
				case "Citrix":
					this.spawnCitrixWindow(descriptor);
					data = { ...result };
					break;
				case "external":
					this.spawnExternalApplication(descriptor);
					data = { ...result };
				default:
			}
		} catch (error) {
			Logger.system.error("error in WindowService.doSpawn", error);
			err = error;
			data = {};
		}

		// makes sure both types of names are set -- will delete later after naming cleanup
		data.name = data.name || data.windowName;
		data.windowName = data.name;

		Logger.system.debug("WindowService.doSpawn-time end", windowType, windowDescriptor.name, data);

		return { err: err, windowIdentifier: data };
	}

	spawnExternalWindow(windowDescriptor): Promise<{ err: any; data: any }> {
		const { customData } = windowDescriptor;
		const windowSpawnTimeout =
			customData.appConfig?.hostManifests?.Finsemble?.window?.options?.windowSpawnTimeout ?? 30; // 30 is default in the schema
		const promiseResolver = (resolve) => {
			function spawnedListener(err, response) {
				Logger.log("onSpawned received for external window", windowDescriptor.name);
				RouterClient.removeListener(`${windowDescriptor.name}.onSpawned`, spawnedListener);
				const fw = windowDescriptor;
				if (!fw.uuid) fw.uuid = response.data?.uuid;
				resolve({ err: null, data: fw });
			}
			setTimeout(() => {
				resolve({ err: `Spawning external window ${windowDescriptor.name} timed out` });
			}, windowSpawnTimeout * 1000);
			RouterClient.addListener(`${windowDescriptor.name}.onSpawned`, spawnedListener);
			RouterClient.query("Assimilation.spawnNative", windowDescriptor, () => {});
		};
		return new Promise(promiseResolver);
	}

	spawnWebWindow(windowDescriptor, spawnSeparateProcess: boolean): StandardPromise<FEAWindow> {
		// This will ensure that the window is actually opened before returning. Seemingly a bug means we
		// can't rely on new System.Window callback. We believe this exhibits for cross-domain windows.
		return new Promise(async (resolve, reject) => {
			if (spawnSeparateProcess) {
				// Remove affinity in case it was set
				delete windowDescriptor.affinity;
				windowDescriptor.spawnAs = "process";
			}
			if (windowDescriptor.external) {
				delete windowDescriptor.preload;
			}
			let feaWindow: FEAWindow;
			const spawnedListener = () => {
				Logger.log("onSpawned received for web window", windowDescriptor.name);
				if (!feaWindow) {
					feaWindow = System.Window.wrap(windowDescriptor.name);
					const closedHandler = () => {
						RouterClient.removeListener(`${windowDescriptor.name}.onSpawned`, spawnedListener);
						feaWindow.removeEventListener("closed", closedHandler);
					};
					feaWindow.addEventListener("closed", closedHandler);

					// We only want to do this for components that are not the Toolbar. The Finsemble Toolbar is a
					// special case that it is handled by the onbeforeunload in the useToolbar.ts hook.
					if (windowDescriptor.componentType !== "Toolbar") {
						// This is where we trap the close-requested when it comes directly from the system (close via taskbar or alt-f4).
						// When we invoke the await wrap.close(), wrap here is a webWindow, we are effectively mimicking the clicking
						// on the close button on the titlebar which will trigger all the relevant event handlers.
						// When closing the window via click on the titlebar it doesn't come through here.
						const closeRequestedHandler = async () => {
							try {
								const { wrap } = await FinsembleWindowInternal.getInstance({
									waitForReady: false,
									uuid: windowDescriptor.uuid,
									name: windowDescriptor.name,
									windowType: windowDescriptor.windowType,
								});

								// if a close-requested event was added by the user we need to trigger that event
								// otherwise we just proceed with the close
								if (Object.keys(wrap.eventManager.remoteListenerEventMap).includes("close-requested")) {
									await wrap.eventManager.trigger("close-requested");
								} else {
									await wrap.close({ removeFromWorkspace: true });
								}
							} catch (error) {
								Logger.system.error("Unable to trigger close-requested event", error);
							}
						};
						feaWindow.addEventListener("close-requested", closeRequestedHandler);
						const closeHandler = () => {
							feaWindow.removeEventListener("closed", closeHandler);
							feaWindow.removeEventListener("close-requested", closeRequestedHandler);
						};
						feaWindow.addEventListener("closed", closeHandler);
					}
				}
				resolve({ err: null, data: feaWindow });
			};

			RouterClient.addListener(`${windowDescriptor.name}.onSpawned`, spawnedListener);

			if (this.isWindows === undefined) {
				this.isWindows = await util.isWindows();
			}

			// Let Finsemble manage minimums and maximums on non-Windows operating systems.
			// There is an electron macOS bug that sends width as 0s if these are set.
			if (!this.isWindows) {
				delete windowDescriptor.minHeight;
				delete windowDescriptor.maxHeight;
				delete windowDescriptor.maxWidth;
				delete windowDescriptor.minWidth;
			}

			System.Window.createNew(
				{
					...windowDescriptor,
					windowName: windowDescriptor.windowName || windowDescriptor.name,
				},
				(err) => {
					if (err) reject(err);
				}
			);
		});
	}

	/**
	 * Helper function that manually adds the path to SelfService.exe with the -qLaunch, as well as params for
	 * API access that are typically added by assimilation (appName, uuid, windowType=Citrix).
	 * Also encodes additional arguments as a query string, since citrix automatically wraps
	 * all arguments in double quotes, which prevents nested double quotes.
	 * @param params Spawn params with name, componentType, arguments, etc.
	 * @see https://support.citrix.com/article/CTX200337 for SelfService.exe details
	 * @returns
	 */
	constructCitrixParams(params) {
		// Citrix automatically wraps all arguments in double quotes, which prevents nested double quotes.
		const appParams = new URLSearchParams(
			`appName=${params.componentType}&uuid=${params.name}&finsembleWindowName=${params.name}&windowType=Citrix&${params.arguments}`
		);
		const selfServiceArgs = `-qlaunch "${params.alias}" "encodedArgs=${appParams.toString()}"`;

		const defaultPath = "C:/Program Files (x86)/Citrix/ICA Client/SelfServicePlugin/SelfService.exe";
		const constructedParams = {
			path: this.manifest.finsemble.citrix?.selfServiceLocation || defaultPath,
			arguments: selfServiceArgs,
		};

		Logger.system.log(
			`Citrix constructed params: path: ${constructedParams.path} arguments: ${constructedParams.arguments}`
		);
		return constructedParams;
	}

	/**
	 * Citrix windows are spawned by electron-adapter's launchExternalProcess childprocess to spawn the app using Citrix's SelfService.exe
	 * allowInternalSpawnByPath allows us to invoke this method internally without requiring the user to globally allow spawning by path.
	 * Currently we only use this to spawn selfservice.exe for Citrix windows.  See MainSystem.launchExternalProcess
	 * @param windowDescriptor
	 */
	spawnCitrixWindow(windowDescriptor) {
		if (!windowDescriptor.alias) {
			Logger.system.error("Spawning virtual apps requires an alias.  Attempted to spawn:", windowDescriptor);
			return;
		}

		let params = this.constructCitrixParams(windowDescriptor);
		System.launchExternalProcess(
			{
				path: params.path,
				arguments: params.arguments,
				allowInternalSpawnByPath: true,
				listener: (event) => {
					// react to close event
					if (event.topic === "exited") {
						Logger.system.log("Citrix window:Exited External Process", event);
					} else {
						Logger.system.log("Citrix window:Running External Process", event);
					}
				},
			} as any, // casting as any because allowInternalSpawnByPath isn't in the ExternalProcessLaunchInfo type
			(payload) => {
				Logger.system.log("payload", payload);
			},
			(err) => {
				Logger.system.error("Error in spawnByPath", err);
			}
		);
	}

	spawnExternalApplication(windowDescriptor) {
		if (!windowDescriptor.path) {
			Logger.system.error("Spawning external apps requires a path.  Attempted to spawn:", windowDescriptor);
			throw "Spawning external apps requires a path.";
		}

		System.launchExternalProcess(
			{
				path: windowDescriptor.path,
				arguments: windowDescriptor.arguments,
				allowInternalSpawnByPath: true,
				listener: (event) => {
					// react to close event
					if (event.topic === "exited") {
						Logger.system.log("External application: Exited External Process", event);
					} else {
						Logger.system.log("External application: Running External Process", event);
					}
				},
			} as any, // casting as any because allowInternalSpawnByPath isn't in the ExternalProcessLaunchInfo type
			(payload) => {
				Logger.system.log("payload", payload);
			},
			(err) => {
				Logger.system.error("Error in spawnExternalApplication", err);
			}
		);
	}
}
